import React from 'react';
import { useIntl } from 'react-intl';
import { music24PlanCapabilities } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../../common/types';
import { planNameMessages } from '../../config/music24PlanNames';
import { NativeUpgradeNote } from '../../components/NativeUpgradeNote/NativeUpgradeNote';
import { TeamOwnerNote } from '../../../team/TeamOwnerNote';
import { useWorkspaceOwner } from '../../../team/useWorkspaceOwner';
import type { BrowseUsageData } from '../hooks/useBrowseUsage';
import { monthlyDollars, plansWithMoreCredits } from '../browseFormat';
import { browseCreditMessages as messages } from '../browseMessages';
import { useBrowseUpgradeAction } from './useBrowseUpgradeAction';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRail.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowsePlanNoteProps {
  usage: BrowseUsageData;
}

/**
 * The foot of the credits panel (design a1, b7): the next plan with more opens a day and "See plans"
 * for the owner, the owner note for a member, the app note in the native web view. Label shows none.
 */
const BrowsePlanNoteComponent: React.FC<BrowsePlanNoteProps> = ({ usage }) => {
  const intl = useIntl();
  const owner = useWorkspaceOwner();
  const next = plansWithMoreCredits(usage.plan)[0];
  const paidPlan = usage.plan !== 'free' && usage.plan !== 'trial';
  const { mode, openPlans, portalUrl } = useBrowseUpgradeAction({
    isOwner: usage.isOwner,
    paidPlan,
    enabled: next !== undefined,
  });

  if (next === undefined) {
    return null;
  }
  if (mode === 'native') {
    return <NativeUpgradeNote />;
  }
  if (mode === 'member') {
    return <TeamOwnerNote owner={owner} />;
  }
  const nextCaps = music24PlanCapabilities[next.tier];
  const planName = intl.formatMessage(planNameMessages[next.tier]);
  const text =
    usage.plan === 'free'
      ? intl.formatMessage(messages.freeUpsell, {
          plan: planName,
          count: nextCaps.dailyBrowseCredits,
          tracks: nextCaps.tracksFollowLimit,
          price: monthlyDollars(next),
        })
      : intl.formatMessage(messages.nextPlan, { plan: planName, count: nextCaps.dailyBrowseCredits });

  return (
    <div className={styles.footLine}>
      <span>{text}</span>
      {portalUrl !== null && portalUrl !== undefined ? (
        <a href={portalUrl} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>
          {intl.formatMessage(messages.seePlans)}
        </a>
      ) : (
        <button type="button" className={styles.linkButton} onClick={openPlans}>
          {intl.formatMessage(messages.seePlans)}
        </button>
      )}
    </div>
  );
};

export const BrowsePlanNote = TypedMemo(BrowsePlanNoteComponent);
